"use client";

import { useState } from "react";
import { Timestamp } from "firebase/firestore";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { Sheet } from "@/components/shell/Sheet";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth/context";
import { useChildMembers } from "@/lib/hooks/useMembers";
import { getFirebaseAuth } from "@/lib/firebase/client";
import { getAppCheckHeader } from "@/lib/firebase/appCheck";
import { createChore } from "@/lib/db/chores";
import { parsePLN } from "@/lib/utils/money";
import { toast } from "@/components/shell/Toaster";
import { cn } from "@/lib/utils/cn";
import type { Member, ChoreInput, ChoreSize } from "@/lib/schemas";

type Props = {
  open: boolean;
  onClose: () => void;
  /** Dzień, na który dodajemy zadanie (preselected schedule.once + dueDate). */
  date: Date | null;
};

const AVATAR_TONE: Record<Member["avatarColor"], string> = {
  rose: "bg-rose-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  violet: "bg-violet-500",
  fuchsia: "bg-fuchsia-500",
};

/**
 * Mini-form do szybkiego dodania chore w trakcie planowania niedzielnego.
 * Tylko najważniejsze pola: title, assignedTo, kind (+reward gdy extra).
 * Wszystko inne defaultowo: schedule=once + dueDate, active=true,
 * requiresApproval=true, offline=false, requiresPhoto=false.
 */
export function QuickAddChoreSheet({ open, onClose, date }: Props) {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const { members: children } = useChildMembers(hid);

  const [title, setTitle] = useState("");
  const [assignedTo, setAssignedTo] = useState<string>("any");
  const [kind, setKind] = useState<"family" | "extra">("family");
  const [rewardZl, setRewardZl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dialogKey = `${open}-${date?.toISOString() ?? ""}`;

  /** AI augmentation przy submit — silent, dobiera ikonę + ulepsza tytuł
      + szacuje size. NIE zmienia stawek. Failure: zwraca oryginalny tytuł,
      bez ikony, size="M". */
  const applyAiAugmentation = async (
    rawTitle: string,
  ): Promise<{ title: string; icon: string | undefined; size: ChoreSize }> => {
    try {
      const user = getFirebaseAuth().currentUser;
      if (!user) return { title: rawTitle, icon: undefined, size: "M" };
      const token = await user.getIdToken();
      const appCheck = await getAppCheckHeader();
      const base =
        import.meta.env.VITE_FUNCTIONS_BASE_URL ||
        "https://europe-central2-kidsduty-53b67.cloudfunctions.net";
      const res = await fetch(`${base}/aiSuggestChore`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...appCheck,
        },
        body: JSON.stringify({ title: rawTitle }),
      });
      if (!res.ok) return { title: rawTitle, icon: undefined, size: "M" };
      const data = (await res.json()) as {
        icon?: string;
        improvedTitle?: string;
        size?: ChoreSize;
      };
      const size: ChoreSize =
        data.size === "S" || data.size === "L" ? data.size : "M";
      return {
        title: data.improvedTitle?.trim() || rawTitle,
        icon: data.icon || undefined,
        size,
      };
    } catch {
      return { title: rawTitle, icon: undefined, size: "M" };
    }
  };

  const handleSubmit = async () => {
    if (!hid || !firebaseUser || !date) return;
    setError(null);

    const trimmed = title.trim();
    if (!trimmed) {
      setError("Wpisz nazwę zadania");
      return;
    }

    let rewardMinor = 0;
    if (kind === "extra") {
      const parsed = parsePLN(rewardZl);
      if (!parsed || parsed < 1) {
        setError("Podaj stawkę (min. 0,01 zł)");
        return;
      }
      rewardMinor = parsed;
    }

    setBusy(true);
    try {
      // AI augmentation: ikona + ulepszony tytuł. Silent fail.
      const augmented = await applyAiAugmentation(trimmed);

      const input: ChoreInput = {
        title: augmented.title,
        ...(augmented.icon ? { icon: augmented.icon } : {}),
        rewardMinor,
        kind,
        offline: false,
        size: augmented.size,
        assignedTo,
        schedule: {
          type: "once",
          dueDate: Timestamp.fromDate(date),
        },
        requiresApproval: true,
        active: true,
      };
      await createChore(hid, firebaseUser.uid, input);
      toast.success(`Dodane na ${format(date, "EEEE", { locale: pl })} ✓`);
      // Reset i zamknij
      setTitle("");
      setKind("family");
      setRewardZl("");
      setAssignedTo("any");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Błąd");
    } finally {
      setBusy(false);
    }
  };

  if (!date) return null;

  const dayLabel = format(date, "EEEE d MMMM", { locale: pl });
  const inputCls =
    "mt-1 w-full h-11 rounded-[12px] border border-line bg-card px-3 text-sm focus:outline-none focus:border-accent";

  return (
    <Sheet
      key={dialogKey}
      open={open}
      onClose={onClose}
      title={`Dodaj zadanie · ${dayLabel}`}
      footer={
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Anuluj
          </Button>
          <Button type="button" variant="accent" onClick={handleSubmit} disabled={busy}>
            {busy ? "Dodaję…" : "Dodaj"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-xs text-muted">Co to za zadanie?</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="np. Pomoc przy obiedzie"
            className={inputCls}
            autoFocus
          />
        </label>

        {/* Kind toggle */}
        <div>
          <span className="text-xs text-muted">Rodzaj</span>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setKind("family")}
              className={cn(
                "h-11 rounded-[12px] border text-[13px] font-semibold transition-colors",
                kind === "family"
                  ? "bg-accent-soft border-accent text-navy ring-1 ring-accent"
                  : "bg-card border-line text-muted",
              )}
            >
              🤝 Pomoc rodzinie
            </button>
            <button
              type="button"
              onClick={() => setKind("extra")}
              className={cn(
                "h-11 rounded-[12px] border text-[13px] font-semibold transition-colors",
                kind === "extra"
                  ? "bg-accent-soft border-accent text-navy ring-1 ring-accent"
                  : "bg-card border-line text-muted",
              )}
            >
              💰 Za stawkę
            </button>
          </div>
        </div>

        {/* Stawka — pokazujemy tylko dla extra */}
        {kind === "extra" && (
          <label className="block">
            <span className="text-xs text-muted">Stawka (zł)</span>
            <input
              value={rewardZl}
              onChange={(e) => setRewardZl(e.target.value)}
              inputMode="decimal"
              placeholder="np. 5,00"
              className={`${inputCls} font-num`}
            />
          </label>
        )}

        {/* Picker dziecka — "wszyscy" + każde dziecko */}
        <div>
          <span className="text-xs text-muted">Dla kogo?</span>
          <div className="mt-1.5 flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setAssignedTo("any")}
              className={cn(
                "shrink-0 px-3 h-11 rounded-[12px] border text-[12px] font-semibold transition-colors",
                assignedTo === "any"
                  ? "bg-accent-soft border-accent text-navy ring-1 ring-accent"
                  : "bg-card border-line text-muted",
              )}
            >
              Wszyscy
            </button>
            {children.map((m) => {
              const tone = AVATAR_TONE[m.avatarColor];
              const active = assignedTo === m.userId;
              return (
                <button
                  key={m.userId}
                  type="button"
                  onClick={() => setAssignedTo(m.userId)}
                  className={cn(
                    "shrink-0 flex items-center gap-2 px-2 h-11 rounded-[12px] border text-[12px] font-semibold transition-colors",
                    active
                      ? "bg-accent-soft border-accent text-navy ring-1 ring-accent"
                      : "bg-card border-line text-muted",
                  )}
                >
                  <span
                    className={cn(
                      "w-7 h-7 rounded-full text-white text-[11px] font-bold flex items-center justify-center",
                      tone,
                    )}
                  >
                    {m.displayName.charAt(0).toUpperCase()}
                  </span>
                  <span>{m.displayName}</span>
                </button>
              );
            })}
          </div>
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}

        <p className="text-[11px] text-muted leading-relaxed">
          ✨ Sianko AI dobierze ikonę i poprawi nazwę przy zapisie. Pełne opcje
          (zdjęcie, fundator) edytujesz później przez „Zadania → ✏️".
        </p>
      </div>
    </Sheet>
  );
}
